import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, Renderer2 } from '@angular/core';
import { AWTPiiKind } from '@aria/webjs-sdk';
import { logError } from '@office-start/telemetry-service';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationActions, KnownInstrumentationEvents, PiiString } from '../../instrumentation/instrumentation.service';
import { DocumentService } from '../../services/document.service';
import { ClientStartupConfigService } from '../../shared-context/client-startup-config.service';

// Directive to capture focus within the target element until destruction
@Directive({
    selector: '[ohpFocusContext]'
})
export class FocusContextDirective implements AfterViewInit, OnDestroy {

    @Input('startId') public startId: string;

    @Input('endId') public endId: string;

    private startTabGuardrail: any;
    private startScanGuardrail: any;
    private endTabGuardrail: any;
    private endScanGuardrail: any;
    private ignoreFocusChanges: boolean;
    private lastFocusedElement: Element | EventTarget;
    private parent: any;

    constructor(
        private readonly documentService: DocumentService,
        private readonly elementRef: ElementRef,
        private readonly instrumentationService: InstrumentationService,
        private readonly renderer: Renderer2,
        private readonly clientStartupConfigService: ClientStartupConfigService
    ) {
        this.ignoreFocusChanges = false;
    }

    public ngAfterViewInit() {
        try {
            this.startTabGuardrail = this.getGuardrail(true);
            this.startScanGuardrail = this.getGuardrail(false);
            this.parent = this.renderer.parentNode(this.elementRef.nativeElement);
            this.renderer.insertBefore(this.parent, this.startTabGuardrail, this.elementRef.nativeElement);
            this.renderer.insertBefore(this.parent, this.startScanGuardrail, this.elementRef.nativeElement);
            const sibling = this.renderer.nextSibling(this.elementRef.nativeElement);
            this.endTabGuardrail = this.getGuardrail(true);
            this.endScanGuardrail = this.getGuardrail(false);
            if (sibling) {
                this.renderer.insertBefore(this.parent, this.endTabGuardrail, sibling);
            } else {
                this.renderer.appendChild(this.parent, this.endTabGuardrail);
            }
            this.renderer.insertBefore(this.parent, this.endScanGuardrail, this.endTabGuardrail);
            this.documentService.nativeDocument.addEventListener('focus', event => this.onFocus(event), true);
            this.lastFocusedElement = this.documentService.nativeDocument.activeElement;
        } catch (e) {
            this.instrumentError(e, KnownInstrumentationActions.Open);
        }
    }

    public ngOnDestroy() {
        this.documentService.nativeDocument.removeEventListener('focus', this.onFocus, true);
        if (!this.parent) {
            return;
        }
        if (this.startTabGuardrail) {
            this.renderer.removeChild(this.parent, this.startTabGuardrail);
        }
        if (this.startScanGuardrail) {
            this.renderer.removeChild(this.parent, this.startScanGuardrail);
        }
        if (this.endTabGuardrail) {
            this.renderer.removeChild(this.parent, this.endTabGuardrail);
        }
        if (this.endScanGuardrail) {
            this.renderer.removeChild(this.parent, this.endScanGuardrail);
        }
    }

    private getGuardrail(tabbable: boolean): any {
        const guardrail = this.renderer.createElement('div');
        this.renderer.setProperty(guardrail, 'tabIndex', tabbable ? 0 : -1);
        this.renderer.setStyle(guardrail, 'width', 0);
        this.renderer.setStyle(guardrail, 'height', 0);
        return guardrail;
    }

    // TODO: Use hostlistener for focus once it supports useCapture functionality: https://github.com/angular/angular/issues/11200
    //@HostListener('document:focus', ['$event'])
    private onFocus(event: FocusEvent) {
        try {
            if (this.ignoreFocusChanges || this.elementRef.nativeElement.contains(event.target)) {
                this.lastFocusedElement = event.target;
                return;
            }
            if (event.target === this.startScanGuardrail) {
                this.focusElement(this.endScanGuardrail);
                return;
            } else if (event.target === this.endScanGuardrail) {
                this.focusElement(this.startScanGuardrail);
                return;
            }
            const startElement = this.elementRef.nativeElement.querySelector(`#${this.startId}`);
            const endElement = this.elementRef.nativeElement.querySelector(`#${this.endId}`);
            if (!startElement || !endElement) {
                this.lastFocusedElement = event.target;
                return;
            }
            this.focusElement(this.lastFocusedElement && this.elementRef.nativeElement.contains(this.lastFocusedElement) && event.target === this.startTabGuardrail ? endElement : startElement);
        } catch (e) {
            this.lastFocusedElement = this.documentService.nativeDocument.activeElement;
            this.instrumentError(e, KnownInstrumentationActions.Focus);
        }
    }

    private focusElement(element: any) {
        this.ignoreFocusChanges = true;
        try {
            element.focus();
        } catch (e) { /* Do nothing */ }
        this.ignoreFocusChanges = false;
    }

    private instrumentError(e: any, action: KnownInstrumentationActions) {
        if (!e) {
            return;
        }
        const params: InstrumentationParams = {};
        params[InstrumentationParam.Error] = new PiiString(e.toString(), this.clientStartupConfigService.config.logPiiInClearText ? AWTPiiKind.NotSet : AWTPiiKind.GenericData);
        params[InstrumentationParam.Action] = action;
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FocusContextError, params);
        logError({
            eventName: 'Focus_Context',
            errorType: 'Focus_Context_Error',
            message: e.toString(),
            debugInfo: `Action: ${action}`
        });
    }
}
